/**
 * Storage + ID utilities for browser clients.
 * Centralized here to avoid ad-hoc copies in app code.
 *
 * Every public function is wrapped in try-catch so that missing or restricted
 * storage APIs (SSR, incognito quota errors, iframes with disabled storage)
 * never propagate exceptions to callers.
 */

/**
 * Return JSON-parsed sessionStorage value or fallback on error/miss.
 *
 * @param key - The sessionStorage key to read.
 * @param fallback - Value returned when the key is missing or parsing fails.
 * @returns The parsed value, or `fallback` on miss/error.
 */
export function getSessionJSON<T>(key: string, fallback: T): T {
  try {
    const raw = globalThis.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (e) {
    console.debug('[core/utils/storage] getSessionJSON(' + key + ') failed', e);
    return fallback;
  }
}

/**
 * Set a JSON-serializable value to sessionStorage; noop on failure.
 *
 * @param key - The sessionStorage key to write.
 * @param value - The value to JSON-serialize and store.
 */
export function setSessionJSON(key: string, value: unknown): void {
  try {
    globalThis.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.debug('[core/utils/storage] setSessionJSON(' + key + ') failed', e);
  }
}

/**
 * Get a string value from localStorage.
 *
 * @param key - The localStorage key to read.
 * @returns The stored string, or `null` on miss/error.
 */
export function getLocal(key: string): string | null {
  try {
    return globalThis.localStorage.getItem(key) ?? null;
  } catch (e) {
    console.debug('[core/utils/storage] getLocal(' + key + ') failed', e);
    return null;
  }
}

/**
 * Set a string value to localStorage; noop on failure.
 *
 * @param key - The localStorage key to write.
 * @param value - The string value to store.
 */
export function setLocal(key: string, value: string): void {
  try {
    globalThis.localStorage.setItem(key, value);
  } catch (e) {
    console.debug('[core/utils/storage] setLocal(' + key + ') failed', e);
  }
}

/**
 * List every localStorage key that starts with `prefix`.
 *
 * @param prefix - The key prefix to match.
 * @returns The matching keys, or an empty list on failure.
 */
export function listLocalKeysByPrefix(prefix: string): string[] {
  try {
    const storage = globalThis.localStorage;
    return Array.from({length: storage.length}, (_, index) =>
      storage.key(index),
    ).filter((key): key is string => key?.startsWith(prefix) ?? false);
  } catch (e) {
    console.debug(
      '[core/utils/storage] listLocalKeysByPrefix(' + prefix + ') failed',
      e,
    );
    return [];
  }
}

/**
 * Remove every localStorage key that starts with `prefix`; noop on failure.
 *
 * @param prefix - The key prefix to remove.
 */
export function removeLocalByPrefix(prefix: string): void {
  try {
    for (const key of listLocalKeysByPrefix(prefix)) {
      globalThis.localStorage.removeItem(key);
    }
  } catch (e) {
    console.debug(
      '[core/utils/storage] removeLocalByPrefix(' + prefix + ') failed',
      e,
    );
  }
}

/** Describes which crypto tier was used to generate an ID. */
export type GenerateIdMethod = 'uuid' | 'crypto-random' | 'time-random';

/**
 * Generate a random, opaque identifier suitable for grouping.
 *
 * Uses the strongest available crypto API, falling back gracefully:
 * `crypto.randomUUID` > `crypto.getRandomValues` > `Date + Math.random`.
 *
 * @param options - Optional configuration.
 * @param options.now - Injected time for the `time-random` fallback. Ignored
 *   when a crypto API is available. When omitted, defaults to `new Date()`.
 * @returns An object with the generated `id` and the `method` used.
 */
export function generateId({now}: {now?: Date} = {}): {
  id: string;
  method: GenerateIdMethod;
} {
  try {
    const {crypto} = globalThis;
    if (typeof crypto.randomUUID === 'function') {
      return {id: crypto.randomUUID(), method: 'uuid'};
    }
    if (typeof crypto.getRandomValues === 'function') {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      const id = Array.from(buf)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      return {id, method: 'crypto-random'};
    }
  } catch (e) {
    console.debug('[core/utils/storage] generateId() crypto fallback', e);
  }
  const ts = (now ?? new Date()).getTime();
  return {
    id: ts.toString(36) + '-' + Math.random().toString(36).slice(2),
    method: 'time-random',
  };
}

/**
 * Module-scoped fallback cache used when `sessionStorage` is unavailable
 * (e.g. sandboxed iframes, incognito quota errors, SSR). Without this cache
 * each call would synthesize a fresh id, silently breaking the "group log
 * entries by session" invariant for the users most likely to hit weird
 * errors. Lives for the page lifetime; cleared on full reload.
 */
const fallbackSessionIds = new Map<
  string,
  {id: string; method: GenerateIdMethod}
>();

/**
 * Get or create a per-session id under the given key.
 *
 * @param key - The sessionStorage key. Defaults to `'cip_session_id_v1'`.
 * @returns The existing or newly generated session ID string.
 */
export function getOrCreateSessionId(key = 'cip_session_id_v1'): string {
  try {
    const existing = globalThis.sessionStorage.getItem(key);
    if (existing) return existing;
    const {id} = generateId();
    globalThis.sessionStorage.setItem(key, id);
    return id;
  } catch (e) {
    console.debug(
      '[core/utils/storage] getOrCreateSessionId(' + key + ') failed',
      e,
    );
    const cached = fallbackSessionIds.get(key);
    if (cached) return cached.id;
    const gen = generateId();
    fallbackSessionIds.set(key, gen);
    return gen.id;
  }
}

/**
 * Get or create a per-device id under the given key.
 *
 * @param key - The localStorage key. Defaults to `'cip_device_id_v1'`.
 * @returns The existing or newly generated device ID string.
 */
export function getOrCreateDeviceId(key = 'cip_device_id_v1'): string {
  const existing = getLocal(key);
  if (existing) return existing;
  const {id} = generateId();
  setLocal(key, id);
  return id;
}

/**
 * Get or create a per-session id, reporting how the id was obtained.
 *
 * @param key - The sessionStorage key. Defaults to `'cip_session_id_v1'`.
 * @returns `{ id, method }` where `method` is `'existing'` if the id was
 *   already in storage, or the generation tier otherwise.
 */
export function getOrCreateSessionIdWithMeta(key = 'cip_session_id_v1'): {
  id: string;
  method: GenerateIdMethod | 'existing';
} {
  try {
    const existing = globalThis.sessionStorage.getItem(key);
    if (existing) return {id: existing, method: 'existing'};
    const gen = generateId();
    globalThis.sessionStorage.setItem(key, gen.id);
    return gen;
  } catch (e) {
    console.debug(
      '[core/utils/storage] getOrCreateSessionIdWithMeta(' + key + ') failed',
      e,
    );
    const cached = fallbackSessionIds.get(key);
    if (cached) return {id: cached.id, method: 'existing'};
    const gen = generateId();
    fallbackSessionIds.set(key, gen);
    return gen;
  }
}

/**
 * Get or create a per-device id, reporting how the id was obtained.
 *
 * @param key - The localStorage key. Defaults to `'cip_device_id_v1'`.
 * @returns `{ id, method }` where `method` is `'existing'` if the id was
 *   already in storage, or the generation tier otherwise.
 */
export function getOrCreateDeviceIdWithMeta(key = 'cip_device_id_v1'): {
  id: string;
  method: GenerateIdMethod | 'existing';
} {
  const existing = getLocal(key);
  if (existing) return {id: existing, method: 'existing'};
  const gen = generateId();
  setLocal(key, gen.id);
  return gen;
}
